import React from "react";
import { format, parseISO } from "date-fns";
import { Calendar as CalendarIcon } from "lucide-react";
import { useEventSessionForm } from "@/modules/eventRegistration/features/EventSessionManagement/hooks/useEventSessionForm";

import {
  Form,
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormMessage,
} from "@/components/ui/form";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import CustomCleaveInput from "@/components/CustomCleaveInput/CustomCleaveInput";
import { Input } from "@/components/ui/input";
import { DatePicker } from "@/components/DatePicker/DatePicker";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

import { EntitySingleSelect } from "@/components/EntitySingleSelect/EntitySingleSelect";
import { searchEventInstructors } from "@/modules/eventRegistration/features/EventInstructorManagement/api/searchEventInstructors/searchEventInstructorsApi";
import { getEventVenueLookupApi } from "@/api/getEventVenueLookup/getEventVenueLookupApi";
import { GetEventVenueLookupRequest } from "@/api/getEventVenueLookup/types";

interface EventSessionFormProps {
  isEditMode?: boolean;
  eventUuid: string;
  sessionUuid?: string | null;
  onSuccessCallback?: () => void;
  isFinalized?: boolean;
}

function EventSessionForm({
  isEditMode = false,
  eventUuid,
  sessionUuid,
  onSuccessCallback,
  isFinalized = false,
}: EventSessionFormProps) {
  const {
    formMethods,
    handleSubmit,
    control,
    isSubmitting,
    fields,
    timezones,
    isTimezonesLoading,
    maxEnrollmentsValue,
    isVirtualOnlyValue,
    selectedTimezone,
    timeOptions,
    handleDateChange,
    parseIsoString,
    updateSessionDayTime,
    handleFormSubmit,
    getDisplayPropertiesForSessionDay,
    isLoading,
  } = useEventSessionForm({
    isEditMode,
    eventUuid,
    sessionUuid,
    onSuccessCallback,
  });

  return (
    <Form {...formMethods}>
      <form className="space-y-4" onSubmit={handleSubmit(handleFormSubmit)}>
        <FormField
          control={control}
          name="name"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Session Name</FormLabel>
              <FormControl>
                <Input
                  {...field}
                  disabled={isFinalized}
                  placeholder="Enter session name"
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={control}
          name="timezoneId"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Timezone</FormLabel>
              <FormControl>
                <select
                  className="block w-full rounded-md border border-gray-300 dark:border-slate-700 bg-white dark:bg-slate-800 dark:text-slate-100 py-2 px-3 text-sm"
                  disabled={isTimezonesLoading || isFinalized}
                  onChange={(e) => {
                    const numericVal = parseInt(e.target.value, 10);
                    field.onChange(isNaN(numericVal) ? undefined : numericVal);
                  }}
                  value={field.value ?? ""}
                >
                  {isTimezonesLoading ? (
                    <option value="">Loading timezones...</option>
                  ) : (
                    <>
                      <option value="">-- Select a time zone --</option>
                      {timezones.map((tz) => (
                        <option key={tz.id} value={tz.id}>
                          {tz.label}
                        </option>
                      ))}
                    </>
                  )}
                </select>
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={control}
          name="startDate"
          render={({ field }) => {
            const dateValue = parseIsoString(field.value);
            return (
              <FormItem>
                <FormLabel>Start Date/Time</FormLabel>
                <FormControl>
                  <DatePicker
                    disabled={isFinalized}
                    onChange={handleDateChange("startDate")}
                    placeholder="Pick a start date/time"
                    showTimeSelect
                    timeZone={selectedTimezone?.value}
                    value={dateValue}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            );
          }}
        />

        <FormField
          control={control}
          name="endDate"
          render={({ field }) => {
            const dateValue = parseIsoString(field.value);
            return (
              <FormItem>
                <FormLabel>End Date/Time</FormLabel>
                <FormControl>
                  <DatePicker
                    disabled={isFinalized}
                    onChange={handleDateChange("endDate")}
                    placeholder="Pick an end date/time"
                    showTimeSelect
                    timeZone={selectedTimezone?.value}
                    value={dateValue}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            );
          }}
        />

        {fields.length > 0 && (
          <div className="space-y-4">
            <div className="space-y-1">
              <FormLabel>Daily Schedule</FormLabel>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Set the start and end times for each day in your selected date
                range.
              </p>
            </div>

            <div className="space-y-3">
              {fields.map((field, index) => {
                const displayProps = getDisplayPropertiesForSessionDay(field);

                return (
                  <div
                    className="flex items-center rounded-lg border dark:border-slate-700 p-3"
                    key={field.id}
                  >
                    <div className="flex items-center gap-2 h-8">
                      <CalendarIcon className="h-4 w-4 text-gray-500 dark:text-gray-400" />
                      <span className="text-sm text-gray-900 dark:text-white font-medium w-20">
                        {displayProps.date
                          ? format(parseISO(displayProps.date), "EEE, MMM d")
                          : ""}
                      </span>
                      <div className="flex items-center gap-3 ml-2">
                        <div className="w-[100px] h-8 flex items-center">
                          <div className="w-full space-y-0">
                            <Select
                              disabled={isFinalized}
                              onValueChange={(value) =>
                                updateSessionDayTime(index, "start", value)
                              }
                              value={displayProps.startTime}
                            >
                              <SelectTrigger className="h-8 text-sm w-[105px]">
                                <SelectValue placeholder="Start time" />
                              </SelectTrigger>
                              <SelectContent align="start">
                                {timeOptions.map((option) => (
                                  <SelectItem
                                    key={option.value}
                                    value={option.value}
                                  >
                                    {option.label}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                        </div>

                        <span className="text-gray-500 dark:text-gray-400 text-sm flex-shrink-0 h-8 flex items-center">
                          to
                        </span>

                        <div className="w-[100px] h-8 flex items-center">
                          <div className="w-full space-y-0">
                            <Select
                              disabled={isFinalized}
                              onValueChange={(value) =>
                                updateSessionDayTime(index, "end", value)
                              }
                              value={displayProps.endTime}
                            >
                              <SelectTrigger className="h-8 text-sm w-[105px]">
                                <SelectValue placeholder="End time" />
                              </SelectTrigger>
                              <SelectContent align="start">
                                {timeOptions.map((option) => (
                                  <SelectItem
                                    key={option.value}
                                    value={option.value}
                                  >
                                    {option.label}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        <FormField
          control={control}
          name="isVirtualOnly"
          render={({ field }) => (
            <FormItem className="flex items-center justify-between rounded-lg border dark:border-slate-700 p-4">
              <div className="space-y-0.5">
                <FormLabel className="text-base">Virtual-Only</FormLabel>
              </div>
              <FormControl>
                <Switch
                  checked={Boolean(field.value)}
                  disabled={isFinalized}
                  onCheckedChange={field.onChange}
                />
              </FormControl>
            </FormItem>
          )}
        />

        {!isVirtualOnlyValue && (
          <FormField
            control={control}
            name="venueId"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Venue</FormLabel>
                <FormControl>
                  <EntitySingleSelect
                    disabled={isFinalized}
                    entityNamePlural="Venues"
                    entityNameSingular="Venue"
                    fetchEntities={async ({ searchTerm, page, pageSize }) => {
                      const sortBy = "name";
                      const sortOrder = "asc";
                      const result = await getEventVenueLookupApi({
                        isActive: true,
                        searchTerm,
                        page,
                        pageSize,
                        sortBy,
                        sortOrder,
                      } as GetEventVenueLookupRequest);

                      return {
                        data: result.data.map((venue) => ({
                          id: venue.id,
                          name: venue.name,
                        })),
                        totalCount: result.meta.totalCount,
                      };
                    }}
                    onChange={field.onChange}
                    value={field.value || null}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        )}

        <FormField
          control={control}
          name="instructorId"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Instructor</FormLabel>
              <FormControl>
                <EntitySingleSelect
                  disabled={isFinalized}
                  entityNamePlural="Instructors"
                  entityNameSingular="Instructor"
                  fetchEntities={async ({ searchTerm, page, pageSize }) => {
                    const sortBy = "name";
                    const sortOrder = "ASC";
                    const result = await searchEventInstructors({
                      searchTerm,
                      page,
                      pageSize,
                      sortBy,
                      sortOrder,
                    });
                    const { instructors, totalCount } = result.data;

                    return {
                      data: instructors.map((inst) => ({
                        id: inst.id,
                        name: inst.name,
                      })),
                      totalCount,
                    };
                  }}
                  onChange={field.onChange}
                  value={field.value || null}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={control}
          name="maxEnrollments"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Max Enrollments</FormLabel>
              <FormControl>
                <CustomCleaveInput
                  className="flex h-9 w-full rounded-md border border-input bg-white px-3 py-1 text-base shadow-sm transition-colors placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50 md:text-sm dark:bg-slate-800 dark:border-slate-700 dark:text-slate-100"
                  disabled={isFinalized}
                  onChange={(rawValue: string) => {
                    const parsedInt = parseInt(rawValue.replace(/\D/g, ""), 10);
                    field.onChange(isNaN(parsedInt) ? 0 : parsedInt);
                  }}
                  options={{
                    numeral: true,
                    numeralDecimalScale: 0,
                    numeralThousandsGroupStyle: "thousand",
                    numeralPositiveOnly: true,
                    prefix: "",
                    rawValueTrimPrefix: false,
                  }}
                  placeholder="0"
                  value={String(field.value ?? "")}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={control}
          name="virtualLink"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Virtual Link</FormLabel>
              <FormControl>
                <Input
                  {...field}
                  disabled={isFinalized}
                  placeholder="https://example.com/meeting"
                  type="url"
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={control}
          name="notes"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Notes</FormLabel>
              <FormControl>
                <Textarea
                  {...field}
                  disabled={isFinalized}
                  placeholder="Additional info..."
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={control}
          name="isPublished"
          render={({ field }) => (
            <FormItem className="flex items-center justify-between rounded-lg border dark:border-slate-700 p-4">
              <div className="space-y-0.5">
                <FormLabel className="text-base">Published</FormLabel>
              </div>
              <FormControl>
                <Switch
                  checked={Boolean(field.value)}
                  onCheckedChange={field.onChange}
                />
              </FormControl>
            </FormItem>
          )}
        />

        <div className="flex justify-end">
          <Button
            disabled={isLoading || isSubmitting || maxEnrollmentsValue < 0}
            type="submit"
          >
            {isLoading || isSubmitting ? "Saving..." : "Save"}
          </Button>
        </div>
      </form>
    </Form>
  );
}

export default EventSessionForm;
